// Referral-link capture + claim. Mirrors signupAttribution.ts (Task #618):
// when a visitor lands with ?ref=CODE we remember the code in localStorage
// (7-day TTL); after they sign in, App.tsx calls claimReferral() which POSTs
// the code to /api/referrals/claim. The server only records it for accounts
// created <48h ago and never twice per user, so this is safe to fire
// opportunistically on every sign-in.

const KEY = 'smg_referral_v1';
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** Capture ?ref=CODE from the landing URL, if present. Call once at boot. */
export function rememberReferralCode(): void {
  try {
    const code = new URLSearchParams(window.location.search).get('ref');
    if (!code || !/^[A-Za-z0-9]{4,20}$/.test(code)) return;
    localStorage.setItem(KEY, JSON.stringify({ code: code.toUpperCase(), at: Date.now() }));
  } catch { /* storage unavailable — attribution is best-effort */ }
}

/** Claim any remembered referral for the signed-in user. Clears the marker
 *  on any definitive server response; keeps it only on network/5xx failure
 *  so a cold-start blip can retry on the next sign-in. */
export async function claimReferral(): Promise<void> {
  let code: string;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as { code?: unknown; at?: unknown };
    if (typeof parsed.code !== 'string' || typeof parsed.at !== 'number'
      || Date.now() - parsed.at > TTL_MS) {
      localStorage.removeItem(KEY);
      return;
    }
    code = parsed.code;
  } catch {
    try { localStorage.removeItem(KEY); } catch { /* noop */ }
    return;
  }
  try {
    const r = await fetch('/api/referrals/claim', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ code }),
    });
    if (r.status >= 500) return; // transient — retry next sign-in
    localStorage.removeItem(KEY);
  } catch { /* network failure — keep marker for retry */ }
}
